import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GameService from '../services/GameService';
import allGames from '../data/workingGames';

const AllGamesPage = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGames, setLoadingGames] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const gamesPerPage = 24;
  const prevFilteredLengthRef = useRef(0);

  // Game categories - Updated to include all types from comprehensive CSV database
  const categories = [
    'All',
    'Slot Game',
    'CasinoLive',
    'Table Game',
    'Card Game',
    'Crash Game',
    'Fish Game',
    'Sports Game',
    'VirtualSports',
    'Bingo',
    'Casino'
  ];

  // All games are now filtered to show only working games
  // This includes only the verified working games from test results

  useEffect(() => {
    // Load working games array
    const loadWorkingGames = async () => {
      try {
        // Use the working games array (verified to be working)
        setGames(allGames);
        setLoading(false);
      } catch (error) {
        console.error('Error loading working games:', error);
        setLoading(false);
      }
    };
    
    loadWorkingGames();
  }, []);

  // Filter games based on category and search term
  const filteredGames = useMemo(() => {
    let filtered = games;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(game => game.type === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(game => 
        game.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [games, selectedCategory, searchTerm]);

  // Reset to page 1 if current page is out of range after filtering
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredGames.length / gamesPerPage));
    
    // Only update page if filtered games length changed
    if (filteredGames.length !== prevFilteredLengthRef.current) {
      prevFilteredLengthRef.current = filteredGames.length;
      
      // Check if current page is out of range
      setCurrentPage(prevPage => {
        const maxPageValue = Math.max(1, Math.ceil(filteredGames.length / gamesPerPage));
        return prevPage > maxPageValue ? maxPageValue : prevPage;
      });
    }
  }, [filteredGames.length, gamesPerPage]);

  // Pagination
  const totalPages = Math.ceil(filteredGames.length / gamesPerPage);
  const startIndex = (currentPage - 1) * gamesPerPage;
  const endIndex = startIndex + gamesPerPage;
  const currentGames = filteredGames.slice(startIndex, endIndex);

  // Generate pagination page numbers with ellipsis
  const getPaginationPages = () => {
    const pages = [];
    const maxVisible = 7; // Maximum number of page buttons to show
    
    if (totalPages <= maxVisible) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate range around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if near the beginning
      if (currentPage <= 3) {
        end = Math.min(4, totalPages - 1);
      }
      
      // Adjust if near the end
      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 3);
      }
      
      // Add ellipsis and pages
      if (start > 2) {
        pages.push('ellipsis-start');
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Helper function to convert game name to slug
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '-') // Replace special characters with hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
      .trim();
  };

  const handleGameClick = async (game) => {
    try {
      setLoadingGames(prev => ({ ...prev, [game.id]: true }));
      
      // Check if user is logged in
      const user = localStorage.getItem('reddyannabook_user');
      if (!user) {
        alert('Please login to play games.');
        navigate('/login');
        return;
      }

      // Parse user data
      const userData = JSON.parse(user);
      console.log('🎮 Launching game with user data:', userData);
      
      // Generate slug from game name for backend API
      const gameSlug = generateSlug(game.name);
      console.log('🎯 Game details:', {
        name: game.name,
        slug: gameSlug,
        uid: game.uid,
        provider: game.provider,
        type: game.type
      });
      
      // Launch the game - use slug as gameType for HUIDU games
      const response = await GameService.launchGame(gameSlug, userData);
      
      if (response.success && response.data.game_url) {
        console.log('✅ Game launched successfully:', response.data);
        // Navigate to game webview with the game URL
        navigate('/game', {
          state: {
            gameUrl: response.data.game_url,
            gameTitle: game.name
          }
        });
      } else {
        throw new Error(response.error || 'Failed to launch game');
      }
    } catch (error) {
      console.error('❌ Game launch error for:', game.name);
      console.error('Error details:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Failed to launch game. Please try again.';
      
      if (error.message) {
        // Check for specific error types
        if (error.message.includes('does not exist') || error.message.includes('Evolution') || error.message.includes('evo/')) {
          errorMessage = `${game.name} is currently unavailable. This ${game.provider} game requires additional setup. Please try another game.`;
        } else if (error.message.includes('Invalid game type')) {
          errorMessage = `${game.name} is not available yet. We're working on adding more ${game.provider} games. Please try another game!`;
        } else if (error.message.includes('Authentication') || error.message.includes('login')) {
          errorMessage = `Please login to play ${game.name}.`;
        } else {
          errorMessage = `Unable to launch ${game.name}. Please try another game or contact support.`;
        }
      }
      
      alert(errorMessage);
    } finally {
      setLoadingGames(prev => ({ ...prev, [game.id]: false }));
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading games...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">All Games</h1>
            <p className="text-sm text-gray-600 mt-1">{games.length > 0 ? `${games.length} verified working games` : 'Loading games...'}</p>
          </div>
          
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search games..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Games Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {currentGames.map((game) => (
            <div
              key={game.id}
              className="bg-white rounded-lg border shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleGameClick(game)}
            >
              <div className="aspect-square w-full overflow-hidden relative">
                <img
                  src={game.image}
                  alt={game.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                {/* Placeholder with game name text */}
                <div 
                  className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex flex-col items-center justify-center text-center p-2"
                  style={{ display: 'none' }}
                >
                  <div className="text-gray-700 font-bold text-sm leading-tight mb-1">
                    {game.name}
                  </div>
                  <div className="text-gray-500 text-xs">
                    {game.type}
                  </div>
                </div>
                {loadingGames[game.id] && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="text-white text-sm">Loading...</div>
                  </div>
                )}
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-sm text-gray-800 truncate">{game.name}</h3>
                <p className="text-xs text-gray-500">{game.type}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 flex-wrap">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="First page"
            >
              ««
            </button>
            
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            
            {getPaginationPages().map((page, index) => (
              page === 'ellipsis-start' || page === 'ellipsis-end' ? (
                <span key={`ellipsis-${index}`} className="px-2 py-2 text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 min-w-[40px] border rounded-md text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              )
            ))}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
            
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Last page"
            >
              »»
            </button>
          </div>
        )}

        {/* Results Info */}
        <div className="text-center text-gray-600 mt-4">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredGames.length)} of {filteredGames.length} games
        </div>
      </div>
    </div>
  );
};

export default AllGamesPage;
